export class ProductInfo extends HTMLElement {
  abortController = undefined;
  swiper = undefined;
  mainSwiper = undefined;
  thumbsSwiper = undefined;
  carouselSwiper = undefined;

  constructor() {
    super();
  }

  setupEventListeners() {
    this.variantSelector?.addEventListener('change', this.onVariantChange.bind(this));
    if (this.quantitySelector) {
      const onQuantityEvent = this.onQuantitySelectorEvent.bind(this);
      this.quantitySelector.addEventListener('change', onQuantityEvent);
      this.quantitySelector.querySelectorAll('button[name="plus"], button[name="minus"]').forEach((button) => {
        button.addEventListener('click', onQuantityEvent);
      });
      this.quantitySelector
        .querySelector('input[type="number"]')
        ?.addEventListener('keydown', this.onQuantitySelectorKeydown.bind(this));
    }
  }

  connectedCallback() {
    this.setupEventListeners();
    this.initSwiper();
  }

  _syncGalleryNavState(mainSwiper, thumbsPrev, thumbsNext) {
    if (!mainSwiper) return;
    const atBeginning = mainSwiper.isBeginning;
    const atEnd = mainSwiper.isEnd;
    const buttons = [
      { button: thumbsPrev, disabled: atBeginning },
      { button: thumbsNext, disabled: atEnd },
    ];

    buttons.forEach(({ button, disabled }) => {
      if (!(button instanceof HTMLElement)) return;
      const wasFocused = document.activeElement === button;
      button.classList.toggle('swiper-button-disabled', disabled);
      button.toggleAttribute('disabled', disabled);
      button.setAttribute('aria-disabled', disabled ? 'true' : 'false');

      // Native disabled removes keyboard focus — move to the other nav control when needed.
      if (disabled && wasFocused) {
        const other = button === thumbsPrev ? thumbsNext : thumbsPrev;
        if (other instanceof HTMLElement && !other.hasAttribute('disabled')) {
          other.focus();
        }
      }
    });
  }

  initSwiper() {
    const thumbnailGalleryEl = this.querySelector('.product-media-gallery__thumbnails');
    const thumbnailWrapperEl = this.querySelector('.product-media-gallery__thumbnails-wrapper');
    if (thumbnailGalleryEl) {
      const mainGalleryEl = this.querySelector('.product-media-gallery__main');
      if (mainGalleryEl) {
        const thumbsPrev = thumbnailWrapperEl?.querySelector(
          '.product-media-gallery__nav--thumbs.product-media-gallery__nav--prev'
        );
        const thumbsNext = thumbnailWrapperEl?.querySelector(
          '.product-media-gallery__nav--thumbs.product-media-gallery__nav--next'
        );

        this.thumbsSwiper = new Swiper(thumbnailGalleryEl, {
          spaceBetween: 0,
          slidesPerView: 4.1,
          freeMode: true,
          watchSlidesProgress: true,
          slideToClickedSlide: true,
          breakpoints: {
            768: {
              slidesPerView: 'auto',
              spaceBetween: 0,
            },
          },
        });

        this.mainSwiper = new Swiper(mainGalleryEl, {
          spaceBetween: 0,
          navigation: {
            prevEl: mainGalleryEl.querySelector('.product-media-gallery__nav--main.product-media-gallery__nav--prev'),
            nextEl: mainGalleryEl.querySelector('.product-media-gallery__nav--main.product-media-gallery__nav--next'),
          },
          thumbs: {
            swiper: this.thumbsSwiper,
          },
        });
        this.swiper = this.mainSwiper;

        // Thumbnail-row arrows advance the main gallery so both stay in sync.
        const onThumbsPrev = (event) => {
          event.preventDefault();
          this.mainSwiper?.slidePrev();
        };
        const onThumbsNext = (event) => {
          event.preventDefault();
          this.mainSwiper?.slideNext();
        };
        thumbsPrev?.addEventListener('click', onThumbsPrev);
        thumbsNext?.addEventListener('click', onThumbsNext);

        const syncThumbsNav = () => this._syncGalleryNavState(this.mainSwiper, thumbsPrev, thumbsNext);
        this.mainSwiper.on('slideChange', syncThumbsNav);
        this.mainSwiper.on('reachBeginning', syncThumbsNav);
        this.mainSwiper.on('reachEnd', syncThumbsNav);
        this.mainSwiper.on('fromEdge', syncThumbsNav);
        syncThumbsNav();
      }
    }

    const carouselGalleryEl = this.querySelector('.product-media-gallery__carousel');
    if (carouselGalleryEl) {
      this.carouselSwiper = new Swiper(carouselGalleryEl, {
        autoHeight: true,
        direction: 'horizontal',
        pagination: {
          el: carouselGalleryEl.querySelector('.swiper-pagination'),
        },
        navigation: {
          prevEl: carouselGalleryEl.querySelector('.swiper-button-prev'),
          nextEl: carouselGalleryEl.querySelector('.swiper-button-next'),
        },
      });
      if (!this.mainSwiper) {
        this.swiper = this.carouselSwiper;
      }
    }
  }

  get variantSelector() {
    return this.querySelector('variant-selector');
  }

  get quantitySelector() {
    return this.querySelector('quantity-selector');
  }

  get selectedOptionValues() {
    if (this.variantSelector.dataset.pickerType === 'dropdown') {
      const list = Array.from(this.variantSelector.querySelectorAll('select')).map(
        (select) => select.options[select.selectedIndex].dataset.optionValueId
      );
      return list;
    } else {
      const list = Array.from(this.variantSelector.querySelectorAll('fieldset input:checked')).map(
        ({ dataset }) => dataset.optionValueId
      );
      return list;
    }
  }

  getSelectedVariant(html) {
    const selectedVariant = html.querySelector('[data-selected-variant]')?.innerHTML;
    return !!selectedVariant ? JSON.parse(selectedVariant) : null;
  }

  onVariantChange(e) {
    const productUrlChanged = e.target?.dataset?.productUrl ? (e.target?.dataset?.productUrl !== this.dataset.url) : false;
    const productUrl = e.target?.dataset?.productUrl || this.dataset.url;
    this.renderSection(productUrlChanged, productUrl);
  }

  onQuantitySelectorKeydown(e) {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;

    e.preventDefault();
    const button = this.quantitySelector.querySelector(
      e.key === 'ArrowUp' ? 'button[name="plus"]' : 'button[name="minus"]'
    );
    if (!button) return;

    this.onQuantitySelectorEvent({
      type: 'click',
      currentTarget: button,
      target: button,
    });
  }

  onQuantitySelectorEvent(e) {
    if (!this.quantitySelector) return;
    const quantityInput = this.quantitySelector.querySelector('input[type="number"]');
    if (!quantityInput) return;

    const currentValue = parseInt(quantityInput.value, 10) || 0;
    const minValue = parseInt(quantityInput.getAttribute('min'), 10) || 0;
    const maxValue = parseInt(quantityInput.getAttribute('max'), 10) || Infinity;
    const button = e.currentTarget?.name ? e.currentTarget : e.target.closest?.('button');
    const action = button?.name || e.target.name;
    let nextValue = currentValue;

    if (action === 'minus' && currentValue > minValue) {
      nextValue = currentValue - 1;
    } else if (action === 'plus' && currentValue < maxValue) {
      nextValue = currentValue + 1;
    } else if (e.type === 'change') {
      if (currentValue < minValue) {
        nextValue = minValue;
      } else if (currentValue > maxValue) {
        nextValue = maxValue;
      }
    } else {
      return;
    }

    if (nextValue === currentValue) return;

    quantityInput.value = nextValue;
    if (e.type !== 'change') {
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  updateMedia(variantFeaturedMediaId) {
    if (!variantFeaturedMediaId) return;
    const mediaSlide = this.querySelector(`.swiper-slide[data-media-id="${variantFeaturedMediaId}"]`);
    if (!mediaSlide) return;

    const slideIndex = parseInt(mediaSlide.dataset.mediaIndex, 10);
    if (Number.isNaN(slideIndex)) return;

    this.mainSwiper?.slideTo(slideIndex);
    this.carouselSwiper?.slideTo(slideIndex);
    if (!this.mainSwiper && !this.carouselSwiper) {
      this.swiper?.slideTo(slideIndex);
    }
  }

  updateURL(variantId) {
    // this.querySelector('share-button')?.updateUrl(
    //   `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
    // );

    // Don't update URL if this is in a modal/quick-add context
    if (this.dataset.updateUrl === 'false') return;

    if (!window.location.pathname.includes('/products/')) return;
    window.history.replaceState({}, '', `${this.dataset.url}${variantId ? `?variant=${variantId}` : ''}`);
  }

  updateSourceFromDestination = (html, id, { syncAttributes = false } = {}) => {
    const source = html.getElementById(`${id}`);
    const destination = this.querySelector(`#${id}`);
    if (source && destination) {
      destination.innerHTML = source.innerHTML;

      if (syncAttributes) {
        destination.className = source.className;

        if (source.hasAttribute('aria-hidden')) {
          destination.setAttribute('aria-hidden', source.getAttribute('aria-hidden'));
        } else {
          destination.removeAttribute('aria-hidden');
        }
      }
    }
  };

  updateVariantInputs(variantId) {
    this.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`).forEach(
      (productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        input.value = variantId ?? '';
      }
    );
  }

  renderSection(productUrlChanged, productUrl) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    // If the section is in a modal, use the original section id without the modal suffix
    const sectionId = this.dataset.updateUrl === 'false' ? this.dataset.section.split('-modal')[0] : this.dataset.section;
    fetch(`${productUrl}?option_values=${this.selectedOptionValues}&section_id=${sectionId}`, {
      signal: this.abortController.signal,
    })
      .then((response) => response.text())
      .then((responseText) => {
        // If the section is in a modal, replace the original section id with the modal section id
        if (this.dataset.updateUrl === 'false') {
          responseText = responseText.replaceAll(this.dataset.section.split('-modal')[0], this.dataset.section);
        }

        // Parse the response text into an HTML document
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const variant = this.getSelectedVariant(html);
        if (productUrlChanged) {
          // If the product url has changed, replace the current section with the new section
          const productInfo = html.querySelector('product-info');
          this.replaceWith(productInfo);
          productInfo.updateURL(variant?.id);
        } else {
          this.updateMedia(variant?.featured_media?.id);
          this.updateURL(variant?.id);
          this.updateVariantInputs(variant?.id);
          this.updateSourceFromDestination(html, `add-to-cart-container-${this.dataset.section}`);
          this.updateSourceFromDestination(html, `sticky-add-to-cart-${this.dataset.section}`);
          this.updateSourceFromDestination(html, `variant-selector-${this.dataset.section}`);
          this.updateSourceFromDestination(html, `price-${this.dataset.section}`);
          this.updateSourceFromDestination(html, `sku-${this.dataset.section}`);
          this.updateSourceFromDestination(html, `inventory-${this.dataset.section}`);
          this.updateSourceFromDestination(html, `preorder-note-${this.dataset.section}`, { syncAttributes: true });
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }
}

if (!customElements.get('product-info')) {
  customElements.define('product-info', ProductInfo);
}

if (!customElements.get('variant-selector')) {
  customElements.define('variant-selector', class VariantSelector extends HTMLElement { });
}

if (!customElements.get('quantity-selector')) {
  customElements.define('quantity-selector', class QuantitySelector extends HTMLElement { });
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement { });
}

/**
 * Product info tabs (WAI-ARIA tablist) + sliding underline.
 *
 * Underline behavior mirrors the legacy jQuery `$.fn.ccHoverLine` pattern:
 * - Desktop (≥768px): line follows hovered tab, then returns to the active tab on leave.
 * - Mobile (<768px): line updates on tab activation (click / keyboard).
 * - Resize: line remeasures against the active tab.
 *
 * Positioning values are written to a dedicated <style> tag (not inline styles on the
 * underline element) so appearance stays in CSS vars defined in section-product.css.
 */
class ProductInfoTabs {
  constructor(root) {
    this.root = root;
    this.tablist = this.root.querySelector('[role="tablist"]');
    this.triggers = Array.from(this.root.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.root.querySelectorAll('[role="tabpanel"]'));
    this.hoverLine = null;
    this.lineStyleEl = null;
    this.lineId = null;
    this.desktopMedia = window.matchMedia('(min-width: 768px)');
    this.resizeRaf = null;

    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleTriggerKeydown = this.handleTriggerKeydown.bind(this);
    this.handleTriggerEnter = this.handleTriggerEnter.bind(this);
    this.handleTablistLeave = this.handleTablistLeave.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleDesktopMediaChange = this.handleDesktopMediaChange.bind(this);

    this.initHoverLine();

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', this.handleTriggerClick);
      trigger.addEventListener('keydown', this.handleTriggerKeydown);
    });

    this.bindHoverInteractions();
    window.addEventListener('resize', this.handleResize);

    // matchMedia: prefer addEventListener; fall back for older Safari.
    if (typeof this.desktopMedia.addEventListener === 'function') {
      this.desktopMedia.addEventListener('change', this.handleDesktopMediaChange);
    } else if (typeof this.desktopMedia.addListener === 'function') {
      this.desktopMedia.addListener(this.handleDesktopMediaChange);
    }

    // Wait one frame so layout/fonts are settled before first measure.
    requestAnimationFrame(() => this.updateHoverLine());
  }

  /**
   * Creates the underline node once and a matching <style> rule target.
   * Guarded with `product__info-tabs-nav--initialized` so Theme Editor re-inits do not duplicate the line.
   */
  initHoverLine() {
    if (!(this.tablist instanceof HTMLElement) || this.tablist.classList.contains('product__info-tabs-nav--initialized')) return;

    // Unique id per section instance (supports multiple product sections on a page).
    this.lineId = `product-tabs-line-${this.root.dataset.section || Math.random().toString(36).slice(2)}`;
    this.hoverLine = document.createElement('span');
    this.hoverLine.className = 'product__info-tabs-hover-line';
    this.hoverLine.setAttribute('aria-hidden', 'true');
    this.hoverLine.setAttribute('data-tabs-line', this.lineId);
    this.tablist.appendChild(this.hoverLine);
    this.tablist.classList.add('product__info-tabs-nav--initialized');

    // Measured left/top/width live here — keeps the span free of style="" attributes.
    this.lineStyleEl = document.createElement('style');
    this.lineStyleEl.id = `style-${this.lineId}`;
    document.head.appendChild(this.lineStyleEl);
  }

  /** Active tab used as the underline’s resting position. */
  getActiveTrigger() {
    return (
      this.triggers.find((trigger) => trigger.getAttribute('aria-selected') === 'true') ||
      this.triggers.find((trigger) => trigger.classList.contains('is-active')) ||
      this.triggers[0] ||
      null
    );
  }

  /**
   * Moves/resizes the underline under `trigger` (defaults to the active tab).
   * Updates CSS custom properties consumed by `.product__info-tabs-hover-line`.
   */
  updateHoverLine(trigger = this.getActiveTrigger()) {
    if (!(this.hoverLine instanceof HTMLElement) || !(this.lineStyleEl instanceof HTMLStyleElement)) return;
    if (!(trigger instanceof HTMLElement)) return;

    this.lineStyleEl.textContent = `[data-tabs-line="${this.lineId}"]{--tabs-line-width:${trigger.offsetWidth}px;--tabs-line-left:${trigger.offsetLeft}px;--tabs-line-top:${trigger.offsetTop + trigger.offsetHeight}px;}`;
  }

  /**
   * Desktop-only hover listeners. Re-bound when crossing the 768px breakpoint
   * so mobile does not keep stale mouseenter handlers.
   */
  bindHoverInteractions() {
    if (!(this.tablist instanceof HTMLElement)) return;

    this.triggers.forEach((trigger) => {
      trigger.removeEventListener('mouseenter', this.handleTriggerEnter);
    });
    this.tablist.removeEventListener('mouseleave', this.handleTablistLeave);

    if (this.desktopMedia.matches) {
      this.triggers.forEach((trigger) => {
        trigger.addEventListener('mouseenter', this.handleTriggerEnter);
      });
      this.tablist.addEventListener('mouseleave', this.handleTablistLeave);
    }
  }

  handleTriggerEnter(event) {
    const trigger = event.currentTarget;
    if (!(trigger instanceof HTMLElement)) return;
    this.updateHoverLine(trigger);
  }

  /** Restore underline to the selected tab when the pointer leaves the tablist. */
  handleTablistLeave() {
    this.updateHoverLine();
  }

  handleResize() {
    if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
    this.resizeRaf = requestAnimationFrame(() => {
      this.updateHoverLine();
    });
  }

  handleDesktopMediaChange() {
    this.bindHoverInteractions();
    this.updateHoverLine();
  }

  handleTriggerClick(event) {
    const trigger = event.currentTarget;
    if (!(trigger instanceof HTMLElement)) return;
    this.activate(trigger);
  }

  /**
   * Keyboard support for tabs (WAI-ARIA Tabs pattern):
   * ArrowLeft/ArrowUp, ArrowRight/ArrowDown, Home, End, Enter, Space.
   */
  handleTriggerKeydown(event) {
    const trigger = event.currentTarget;
    if (!(trigger instanceof HTMLElement)) return;

    const currentIndex = this.triggers.indexOf(trigger);
    if (currentIndex < 0 || this.triggers.length === 0) return;

    let nextIndex = currentIndex;
    const key = event.key;

    switch (key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % this.triggers.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (currentIndex - 1 + this.triggers.length) % this.triggers.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.triggers.length - 1;
        break;
      case 'Enter':
      case ' ':
      case 'Spacebar':
        event.preventDefault();
        this.activate(trigger);
        return;
      default:
        return;
    }

    event.preventDefault();
    const nextTrigger = this.triggers[nextIndex];
    nextTrigger.focus();
    this.activate(nextTrigger);
  }

  /** Sync selected tab state, visible panel, and underline position. */
  activate(activeTrigger) {
    const panelId = activeTrigger.getAttribute('aria-controls');

    this.triggers.forEach((trigger) => {
      const isActive = trigger === activeTrigger;
      trigger.classList.toggle('is-active', isActive);
      trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
      trigger.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    this.panels.forEach((panel) => {
      const isActive = panel.id === panelId;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
    });

    this.updateHoverLine(activeTrigger);
  }
}

/** Init once per `[data-product-info-tabs]` root; re-run on Theme Editor section:load. */
function initProductInfoTabs(scope = document) {
  scope.querySelectorAll('[data-product-info-tabs]').forEach((root) => {
    if (root.dataset.tabsInitialized === 'true') return;
    root.dataset.tabsInitialized = 'true';
    new ProductInfoTabs(root);
  });
}

initProductInfoTabs();

document.addEventListener('shopify:section:load', (event) => {
  initProductInfoTabs(event.target);
});